import React, { useMemo } from 'react'
import { useStore } from '@/store'
import { getRegionLabel } from '@/constants'
import { useCatalogStore } from '@/store/catalogStore'

interface Props {
  dimension: string
  drillRegion: string | null | undefined
  itemCount: number
  /** Server-aggregated total streams across items (same semantics as World hop sums). */
  streamTotal?: number
  /** True while a tile is expanded — grid re-ordering is held so the focus tile stays put. */
  orderPaused?: boolean
}

function modeLabel(dimension: string): string {
  if (dimension === 'stream') return 'Streams'
  if (dimension === 'studio') return 'Studios'
  if (dimension === 'game') return 'Games'
  return dimension
}

/** Removable crumb chip rendering one element of the (mode, scope, filters) tuple. */
function Crumb({ label, onClear, title }: { label: string; onClear?: () => void; title?: string }) {
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        fontSize: 10, padding: '2px 8px', borderRadius: 20,
        border: '0.5px solid var(--bd-1)', background: 'var(--bg-3)', color: 'var(--t-0)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
      {onClear && (
        <span
          onClick={onClear}
          role="button"
          aria-label={`Clear ${label}`}
          style={{ cursor: 'pointer', color: 'var(--t-2)', fontWeight: 600 }}
        >
          ×
        </span>
      )}
    </span>
  )
}

export function HexHeader({ dimension, drillRegion, itemCount, streamTotal, orderPaused }: Props) {
  const drillNode = useStore((s) => s.drillNode)
  const filters = useStore((s) => s.filters)
  const clearDrillNode = useStore((s) => s.clearDrillNode)
  const toggleFilterValue = useStore((s) => s.toggleFilterValue)
  const includeInactive = useStore((s) => s.includeInactive)
  const toggleIncludeInactive = useStore((s) => s.toggleIncludeInactive)
  const games = useCatalogStore((s) => s.games)
  const studios = useCatalogStore((s) => s.studios)

  const entityLabel = useMemo(() => {
    const gameNames = new Map(games.map((g) => [g.code, g.name]))
    const studioNames = new Map(studios.map((s) => [s.code, s.name]))
    return (key: 'studio' | 'game', code: string): string => {
      const name = key === 'studio' ? studioNames.get(code) : gameNames.get(code)
      return name ?? code
    }
  }, [games, studios])

  void drillRegion // superseded by drillNode; kept in props for compatibility

  const noun =
    dimension === 'stream' ? 'streams'
      : dimension === 'studio' ? 'studios'
        : dimension === 'game' ? 'games'
          : 'groups'

  const scopeLabel = drillNode
    ? `${drillNode.dimension}: ${getRegionLabel(drillNode.value) || drillNode.value}`
    : 'Global'

  const entityChips: Array<{ key: 'studio' | 'game'; value: string }> = [
    ...filters.studio.map((v) => ({ key: 'studio' as const, value: v })),
    ...filters.game.map((v) => ({ key: 'game' as const, value: v })),
  ]

  return (
    <>
      <div className="panel-head">
        <span className="panel-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          Hex grid · {modeLabel(dimension)}
          <Crumb
            label={scopeLabel}
            onClear={drillNode ? clearDrillNode : undefined}
            title={drillNode ? 'Geo scope — click × to widen to Global' : 'No geo scope'}
          />
          {entityChips.map(({ key, value }) => (
            <Crumb
              key={`${key}:${value}`}
              label={`${key}: ${entityLabel(key, value)}`}
              onClear={() => toggleFilterValue(key, value)}
              title={`${key} filter — click × to remove`}
            />
          ))}
        </span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={toggleIncludeInactive}
            title={includeInactive ? 'All catalog streams matching filters' : 'Live + emitted in last 5 min'}
            style={{
              fontSize: 10, padding: '2px 8px', borderRadius: 'var(--radius)',
              border: `0.5px solid ${includeInactive ? 'var(--info-bd)' : 'var(--bd-1)'}`,
              background: includeInactive ? 'var(--info-bg)' : 'transparent',
              color: includeInactive ? 'var(--info)' : 'var(--t-1)',
              cursor: 'pointer', fontFamily: 'var(--font)',
            }}
          >
            {includeInactive ? 'Include inactive' : 'Active only'}
          </button>
          {orderPaused && (
            <span
              title="A tile is expanded — grid order is held so it stays in place. Collapse to resume sorting."
              style={{
                fontSize: 10, padding: '2px 8px', borderRadius: 20,
                border: '0.5px solid var(--bd-1)', background: 'var(--bg-3)',
                color: 'var(--t-1)', display: 'inline-flex', alignItems: 'center', gap: 5,
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{
                width: 6, height: 6, borderRadius: '50%',
                background: 'var(--warn, #da942a)', display: 'inline-block',
              }} />
              Order paused
            </span>
          )}
          <span style={{ fontSize: 10, color: 'var(--t-2)' }}>
            {dimension === 'stream'
              ? `${streamTotal ?? itemCount} streams`
              : `${itemCount} ${noun}${streamTotal != null ? ` · ${streamTotal} streams` : ''}`}
          </span>
          <span style={{ fontSize: 9, color: 'var(--t-2)', opacity: 0.85 }}>
            Hover or click for details
          </span>
        </span>
      </div>
    </>
  )
}
